import { Controller } from "@hotwired/stimulus";
import { Editor, mergeAttributes, Node, Extension, textInputRule } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from '@tiptap/extension-placeholder'
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import BubbleMenu from "@tiptap/extension-bubble-menu";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import { all, createLowlight } from "lowlight";
import Link from "@tiptap/extension-link";
import Highlight from "@tiptap/extension-highlight";
import Typography from "@tiptap/extension-typography";
import Mention from "@tiptap/extension-mention";
import { Paragraph } from "@tiptap/extension-paragraph";
import OrderedList from "@tiptap/extension-ordered-list";
import BulletList from "@tiptap/extension-bullet-list";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import TrailingNode from "tiptap-extension-trailing-node";
import TextAlign from "@tiptap/extension-text-align";
import ImageResize from "tiptap-extension-resize-image";
import FileHandler from "@tiptap/extension-file-handler";
import HardBreak from '@tiptap/extension-hard-break'
import { Plugin, PluginKey } from "@tiptap/pm/state";
import {createPopper} from "@popperjs/core";
import { CommentLink } from "./comment_link_extension"
import Fuse from 'fuse.js'

export default class extends Controller {
  static targets = ["input", "redactor", "button", "menu", "suggestions", "users", "tmenu", "cmenu", "pickerInput", "colorPreview"];
  static values = {
    editable: {type: Boolean, default: true},
    id: String,
    currentColor: { type: String, default: '#fed7aa' }
  };

  connect() {
    const lowlight = createLowlight(all);
    const that = this;
    let arr = [];
    let len = this.usersTargets.length;
    
    for (let i = 0; i < len; i++) {
      arr.push({
        full_name: this.usersTargets[i].dataset.name,
        uuid: this.usersTargets[i].dataset.id,
        email: this.usersTargets[i].dataset.email
      });
    }
    this.users = arr;
    this.fuse = new Fuse(this.users, {
      keys: ['full_name', 'email'], threshold: 0.6
    });

    let InsTag = Node.create({
      name: "ins",
      content: "text*",
      group: 'inline',
      inline: true,
      addOptions: {
        HTMLAttributes: {},
      },
      parseHTML() {
        return [
          {
            tag: "ins",
          },
        ]
      },
      renderHTML({ HTMLAttributes }) {
        return ["ins", mergeAttributes(HTMLAttributes, {}), 0]
      },
    });

    const CustomParagraph = Paragraph.extend({
      addAttributes() {
        return {
          class: {
            default: null, // Allow any class value
            renderHTML: attributes => {
              if (attributes.class) {
                return { class: attributes.class };
              }
              return {};
            },
          },
        };
      },
    });

    const CustomOrderedList = OrderedList.extend({
      addAttributes() {
        return {
          class: {
            default: null, // Allow any class value
            renderHTML: attributes => {
              if (attributes.class) {
                return { class: attributes.class };
              }
              return {};
            },
          },
        };
      },
    });

    const CustomBulletList = BulletList.extend({
      addAttributes() {
        return {
          class: {
            default: null, // Allow any class value
            renderHTML: attributes => {
              if (attributes.class) {
                return { class: attributes.class };
              }
              return {};
            },
          },
        };
      },
    });

    const CustomTaskList = TaskList.extend({
      addAttributes() {
        return {
          class: {
            default: null, // Allow any class value
            renderHTML: attributes => {
              if (attributes.class) {
                return { class: attributes.class };
              }
              return {};
            },
          },
        };
      },
    });

    const CustomMention = Mention.extend({
      addAttributes() {
        return {
          uuid: {
            default: null,
            parseHTML: (element) => element.getAttribute('data-mention-uuid'),
            renderHTML: (attributes) => {
              return {
                'data-mention-uuid': attributes.uuid
              }
            },
          }
        }
      },
      parseHTML() {
        return [
          {
            tag: "span[data-mention-uuid]"
          }
        ];
      },

      renderHTML({ node, HTMLAttributes }) {
        const user = that.getUserByUuid(node.attrs.uuid);
        let outputText = "Неизвестный пользователь";

        if (user) {
          outputText = `${this.options.suggestion.char}${user.full_name}`;
        }

        return [
          "span",
          mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
          outputText
        ];
      },

      renderText({ node }) {
        const user = that.getUserByUuid(node.attrs.uuid);

        if (user) {
          return `${this.options.suggestion.char}${user.full_name}`;
        }

        return "Неизвестный пользователь";
      }
    });

    const CustomLink = Link.extend({
      addProseMirrorPlugins() {
        return [
          new Plugin({
            key: new PluginKey('linkClickHandler'),
            props: {
              handleDOMEvents: {
                auxclick: (view, event) => {
                  if (event.button === 1) { // Средняя кнопка мыши
                    const target = event.target.closest('a')
                    if (target) {
                      window.open(target.href, '_blank')
                      return true
                    }
                  }
                  return false
                },
                click: (view, event) => {
                  if (event.button === 0) { // Левая кнопка мыши
                    const target = event.target.closest('a')
                    if (target) {
                      event.preventDefault()
                      event.stopPropagation()

                      window.open(target.href, '_blank')
                      return true
                    }
                  }
                  return false
                },
                dragenter: (view, event) => {
                  event.stopPropagation()
                  return false
                },
                dragover: (view, event) => {
                  event.stopPropagation()
                  return false
                },
                drop: (view, event) => {
                  event.stopPropagation()
                  return false
                }
              },
            },
          }),
        ]
      },
    })

    const CustomImageResize = ImageResize.extend({
      addAttributes() {
        return {
          ...this.parent?.(),
          style: {
            default: null,
            parseHTML: element => element.getAttribute('style'),
            renderHTML: attributes => {
              if (!attributes.style) return {};
              return { style: attributes.style };
            },
          },
        };
      },
    });

    const ShiftEnterToParagraph = Extension.create({
      name: 'shiftEnterToParagraph',
      addKeyboardShortcuts() {
        return {
          'Shift-Enter': ({ editor }) => editor.commands.splitBlock(),
        }
      },
    })

    const CustomHardBreak = HardBreak.extend({
      addKeyboardShortcuts() {
        return {}
      },
    })

    const RussianQuotes = Extension.create({
      name: 'russianQuotes',

      addInputRules() {
        return [
          new textInputRule({
            find: /(?:^|[\s\{\[\(\<])(")$/,
            replace: '«',
          }),
          new textInputRule({
            find: /"$/,
            replace: '»',
          }),
        ]
      },
    })

    this.suggestionsTarget.style.display = "none";
    this.focusedSuggetion = 0;

    const menuElement = this.element.querySelector('.bubble-menu') || this.element.querySelector('[data-rich-editor-target="menu"]');
    const suggestionsElement = this.element.querySelector('[data-rich-editor-target="suggestions"]');

    this.redactor = new Editor({
      element: this.redactorTarget,
      extensions: [
        StarterKit.configure({
          codeBlock: false,
          paragraph: false,
          orderedList: false,
          bulletList: false,
          hardBreak: false,
        }),
        InsTag,
        CustomParagraph,
        CustomOrderedList,
        CustomBulletList,
        ShiftEnterToParagraph,
        CustomHardBreak,
        CommentLink,
        CustomImageResize,
        FileHandler.configure({
          allowedMimeTypes: ['image/jpeg', 'image/png', 'image/gif'],
          onDrop: (currentEditor, files, pos) => {
            files.forEach(file => this.uploadImage(file, pos))
          },
          onPaste: (currentEditor, files) => {
            files.forEach(file => this.uploadImage(file))
          },
        }),
        CustomMention.configure({
          HTMLAttributes: {
            class: "mention",
          },
          suggestion: {
            char: '@',
            allowSpaces: true,

            items: ({ query }) => {
              return this.fuse.search(query).map(result => result.item).slice(0, 8)
            },

            render: () => {
              return {
                onStart: props => {
                  this.setSuggetionsProps(props)
                  this.suggestionsStart()
                  this.suggestionsShow()
                  this.suggestionsRender()
                },

                onUpdate: async (props) => {
                  this.setSuggetionsProps(props)
                  this.suggestionsRender()
                },

                onKeyDown: (props) => {
                  if (props.event.key === 'Escape') {
                    return this.suggestionsHide()
                  }

                  if (props.event.key === 'Enter') {
                    return this.suggestionsSelect()
                  }

                  if (props.event.key === 'ArrowUp') {
                    return this.suggestionsUp()
                  }

                  if (props.event.key === 'ArrowDown') {
                    return this.suggestionsDown()
                  }

                  return false
                },

                onExit: () => {
                  this.suggestionsExit()
                },
              }
            }
          },
        }),
        TaskItem.configure({
          nested: true,
        }),
        CustomTaskList,
        Typography.configure({
          openDoubleQuote: false,
          closeDoubleQuote: false
        }),
        RussianQuotes,
        TextAlign.configure({
          types: ['heading', 'paragraph', 'image'],
          alignments: ['left', 'center', 'right'],
          defaultAlignment: 'left',
        }),
        CodeBlockLowlight.configure({
          lowlight,
        }),
        Highlight.configure({ multicolor: true }),
        TrailingNode.configure({
          node: 'paragraph',
          notAfter: ['paragraph'],
        }),
        Placeholder.configure({
          placeholder: "Ваш текст...",
        }),
        BubbleMenu.configure({
          pluginKey: new PluginKey('textMenu'),
          element: this.menuTarget,
          tippyOptions: {
            arrow: false,
            theme: '',
            onCreate(instance) {

              const box = instance.popper.children[0];
              if (box) {
                box.style.backgroundColor = 'transparent';
                box.style.boxShadow = 'none';
                box.style.padding = '0';
                box.style.border = 'none'
              }
            }
          },
          shouldShow: ({ editor, node, from, to }) => {
            const { selection } = editor.state;
            const pos = editor.state.doc.resolve(selection.from);
            const currentNode = pos.parent;

            const isCommentActive = editor.isActive('commentLink')

            if (isCommentActive) {
              const nodeAfter = selection.$from ? selection.$from.nodeAfter : null
              if (nodeAfter && (to - from === nodeAfter.nodeSize)) {
                return false
              }
            }

            return currentNode.type.name != 'doc' && from !== to;
          },
        }),
        Table.configure({
          resizable: true,
        }),
        TableRow,
        TableHeader,
        TableCell,
        CustomLink.configure({
          openOnClick: false,
          HTMLAttributes: {
            target: '_blank',
          },
          autolink: true,
          defaultProtocol: 'https',
          protocols: ['http', 'https'],
          isAllowedUri: (url, ctx) => {
            try {
              const parsedUrl = url.includes(':') ? new URL(url) : new URL(`${ctx.defaultProtocol}://${url}`)

              const disallowedProtocols = ['ftp', 'file', 'mailto']
              const protocol = parsedUrl.protocol.replace(':', '')

              const allowedProtocols = ctx.protocols.map(p => (typeof p === 'string' ? p : p.scheme))

              return ctx.defaultValidate(parsedUrl.href) && !disallowedProtocols.includes(protocol) && allowedProtocols.includes(protocol)
            } catch {
              return false
            }
          },
          shouldAutoLink: url => {
            try {
              const parsedUrl = url.includes(':') ? new URL(url) : new URL(`https://${url}`)
              const disallowedDomains = ['example-no-autolink.com', 'another-no-autolink.com']
              const domain = parsedUrl.hostname

              return !disallowedDomains.includes(domain)
            } catch {
              return false
            }
          },
        }),
      ],
      editorProps: {
        attributes: {
          class: "max-w-none focus:outline-none",
        }
      },
      editable: this.editableValue,
      content: this.inputTarget.value,

      onBeforeCreate: () => {
        const loader = this.redactorTarget.querySelector('.tiptap-loader');
        if (loader) loader.remove();
      },

      onCreate: () => {
        if (menuElement) {
          menuElement.classList.remove("opacity-0", "hidden");
          menuElement.style.display = "";
        }
        if (suggestionsElement) {
          suggestionsElement.classList.remove("opacity-0", "hidden");
        }
      },

      onUpdate: ({ editor }) => {
        this.inputTarget.value = editor.getHTML();
        this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }));
        this.inputTarget.dispatchEvent(new Event('input', { bubbles: true }));
        if (this.hasButtonTarget) {
          this.buttonTarget.disabled = false;
        };
        if (editor.isActive('table')) {
          const view = editor.view;
          const { node: focusedDomElement } = view.domAtPos(editor.state.selection.anchor);
          const table = focusedDomElement.closest('table').getBoundingClientRect();
          this.tmenuTarget.classList.remove("hidden");
          that.tmenuTarget.style.position = "absolute";
          that.tmenuTarget.style.top = `${table.top + window.scrollY - 22}px`;
          that.tmenuTarget.style.left = `${table.right - 130}px`;
        }
        this.dispatchStateChange()
      },
      onFocus: ({ editor }) => {
        if (editor.isActive('table')) {
          const view = editor.view;
          const { node: focusedDomElement } = view.domAtPos(editor.state.selection.anchor);
          const table = focusedDomElement.closest('table').getBoundingClientRect();
          this.tmenuTarget.classList.remove("hidden");
          that.tmenuTarget.style.position = "absolute";
          that.tmenuTarget.style.top = `${table.top + window.scrollY - 25}px`;
          that.tmenuTarget.style.left = `${table.right - 130}px`;
        }
        this.dispatchStateChange()
      },
      onSelectionUpdate: ({ editor }) => {
        if (editor.isActive('table')) {
          const view = editor.view;
          const { node: focusedDomElement } = view.domAtPos(editor.state.selection.anchor);
          if (focusedDomElement instanceof Element) {
            const table = focusedDomElement.closest('table').getBoundingClientRect();
            this.tmenuTarget.classList.remove("hidden");
            that.tmenuTarget.style.position = "absolute";
            that.tmenuTarget.style.top = `${table.top + window.scrollY - 25}px`;
            that.tmenuTarget.style.left = `${table.right - 130}px`;
          }
        }
        this.dispatchStateChange()
      },
    })

    window.addEventListener("editor:set-language", this.handleSetLanguage)
    this.redactorTarget.addEventListener("click", this.handleNativeClick)

    Coloris({
      el: '.highlight-picker',
      wrap: false
    })

    Coloris.setInstance('.highlight-picker', {
      theme: 'default',
      themeMode: 'auto',
      alpha: false,
      swatchesOnly: true,
      margin: 5,
      parent: '.bubble-menu',
      swatches: [
        '#fed7aa',
        '#f5a0a0',
        '#f5ff7f',
        '#77ea9c',
        '#9fc0ff',
        '#e1e1e1'
      ]
    })

    this.syncColorWithDOM(this.currentColorValue)
  }

  disconnect() {
    window.removeEventListener("editor:set-language", this.handleSetLanguage)
    this.redactorTarget.removeEventListener("click", this.handleNativeClick)
    if (this.redactor) this.redactor.destroy()
  }

  handleNativeClick = (event) => {
    this.dispatchStateChange()
  }

  bold() {
    this.redactor.chain().focus().toggleBold().run();
  }

  italic() {
    this.redactor.chain().focus().toggleItalic().run();
  }

  strike() {
    this.redactor.chain().focus().toggleStrike().run();
  }

  bulletList() {
    this.redactor.chain().focus().toggleBulletList().run();
  }

  orderedList() {
    this.redactor.chain().focus().toggleOrderedList().run();
  }

  taskList(){
    this.redactor.chain().focus().toggleTaskList().run()
  }

  header1() {
    this.redactor.chain().focus().toggleHeading({ level: 2 }).run();
  }

  header2() {
    this.redactor.chain().focus().toggleHeading({ level: 3 }).run();
  }

  quote() {
    this.redactor.chain().focus().toggleBlockquote().run();
  }

  codeBlock() {
    this.redactor.chain().focus().toggleCodeBlock().run();
  }

  updateControllerColor(color) {
    if (!color) return

    this.currentColorValue = color

    if (this.hasPickerInputTarget) {
      this.pickerInputTarget.value = color
      this.pickerInputTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }

    if (this.hasColorPreviewTarget) {
      this.colorPreviewTarget.style.backgroundColor = color
    }
  }

  highlite() {
    this.redactor.chain().focus().toggleHighlight({ color: '#fed7aa' }).run()
  }

  syncColorWithDOM(color) {
    if (!color) return

    const previewEl = document.querySelector('.bubble-menu [data-rich-editor-target="colorPreview"]')
    const inputEl = document.querySelector('.bubble-menu [data-rich-editor-target="pickerInput"]')

    if (previewEl) {
      previewEl.style.setProperty('background-color', color, 'important')
    }
    if (inputEl) {
      inputEl.value = color
    }
    if (!previewEl) {
      if (!this.connectionAttempts) this.connectionAttempts = 0
      if (this.connectionAttempts < 5) {
        this.connectionAttempts++
        setTimeout(() => this.syncColorWithDOM(color), 50)
      }
    } else {
      this.connectionAttempts = 0
    }
  }

  applyLastColor() {
    this.redactor.chain().focus().setHighlight({ color: this.currentColorValue }).run()
  }

  applyHighlight(event) {
    const color = event.target.value
    if (!color) return

    this.updateControllerColor(color)

    this.redactor.chain().focus().setHighlight({ color: color }).run()
  }

  removeHighlight() {
    if (this.redactor) {
      this.redactor.chain().focus().unsetHighlight().run()
    }
  }

  getContrastColor(hexColor) {
    const hex = hexColor.replace("#", "")

    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)

    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000

    return yiq < 128 ? "#ffffff" : "#000000"
  }

  table() {
    this.tmenuTarget.classList.remove("hidden");
    this.redactor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }

  // Функции для таблиц
  addColumnBefore(event) {
    this.redactor.chain().focus().addColumnBefore().run();
    event.preventDefault();
  }

  addColumnAfter(event) {
    this.redactor.chain().focus().addColumnAfter().run();
    event.preventDefault();
  }

  deleteColumn(event) {
    this.redactor.chain().focus().deleteColumn().run();
    event.preventDefault();
  }

  addRowBefore(event) {
    this.redactor.chain().focus().addRowBefore().run();
    event.preventDefault();
  }

  addRowAfter(event) {
    this.redactor.chain().focus().addRowAfter().run();
    event.preventDefault();
  }

  deleteRow(event) {
    this.redactor.chain().focus().deleteRow().run();
    event.preventDefault();
  }

  mergeCells(event) {
    this.redactor.chain().focus().mergeCells().run();
    event.preventDefault();
  }

  splitCell(event) {
    this.redactor.chain().focus().splitCell().run();
    event.preventDefault();
  }

  toggleHeaderCell(event) {
    this.redactor.chain().focus().toggleHeaderCell().run();
    event.preventDefault();
  }

  deleteTable(event) {
    this.redactor.chain().focus().deleteTable().run();
    this.tmenuTarget.classList.add("hidden");
    event.preventDefault();
  }

  alignCenter() {
    this.redactor.chain().focus().setTextAlign('center').run();
  }

  alignRight() {
    this.redactor.chain().focus().setTextAlign('right').run();
  }

  alignLeft() {
    this.redactor.chain().focus().unsetTextAlign().run();
  }

  addLocalImage() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        this.uploadImage(file);
      }
    };

    input.click();
  };

  setLink() {
    const previousUrl = this.redactor.getAttributes('link').href
    const url = window.prompt('URL', previousUrl)

    // cancelled
    if (url === null) {
      return
    }

    // empty
    if (url === '') {
      this.redactor.chain().focus().extendMarkRange('link').unsetLink().run()

      return
    }

    // update link
    try {
      this.redactor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    } catch (e) {
      alert(e.message)
    }
  }

  uploadImage(file, pos = null) {
    const formData = new FormData()
    formData.append('image', file)

    fetch('/uploads', {
      method: 'POST',
      body: formData,
      headers: { 'X-CSRF-Token': document.querySelector("meta[name='csrf-token']").content }
    })
      .then(response => response.json())
      .then(data => {
        const chain = this.redactor.chain().focus()


        chain.setImage({ src: data.url })


        chain.run()
      })
  }

  // Функции для поиска и упоминаний

  setSuggetionsProps (props) {
    this.props = props
  }

  suggestionsStart () {
    this.popup = createPopper(this.props.decorationNode, this.suggestionsTarget, {
      placement: 'bottom-start',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 10],
          },
        }
      ]
    })
  }

  suggestionsShow () {
    this.suggestionsTarget.style.display = "block"
  }

  suggestionsUp () {
    this.focusedSuggetion = ((this.focusedSuggetion + this.suggestionsLength) - 1) % this.suggestionsLength;
    this.suggestionsHighlight()
    return true
  }

  suggestionsDown () {
    this.focusedSuggetion = (this.focusedSuggetion === null) ? 0 : (this.focusedSuggetion + 1) % this.suggestionsLength;
    this.suggestionsHighlight()
    return true
  }

  suggestionsHighlight () {
    Array.from(this.suggestionsTarget.children).forEach(
      item => item.classList.remove("is-selected")
    )

    if (! this.selectedSuggetion) {
      return
    }

    this.selectedSuggetion.classList.add("is-selected");
  }

  suggestionsRender () {
    this.suggestionsTarget.innerHTML = this.props.items.map((item) => `
    <button type="button" data-uuid="${item.uuid}" data-action="rich-editor#suggestionsSelectOnClick">
      ${item.full_name}
    </button>
    `).join('')
    this.focusedSuggetion = 0
    this.suggestionsHighlight()
    if (this.props.items.length == 0) {
      this.suggestionsHide();
    }
  }

  suggestionsHide () {
    this.suggestionsTarget.style.display = "none"
    return true
  }

  suggestionsExit () {
    this.popup.destroy()
    this.suggestionsHide()
  }

  suggestionsSelect () {
    this.props.command({
      uuid: this.selectedSuggetion.dataset.uuid,
    })
    return true
  }

  suggestionsSelectOnClick (event) {
    this.props.command({ uuid: event.target.dataset.uuid })
  }

  get selectedSuggetion () {
    return this.suggestionsTarget.children[this.focusedSuggetion]
  }

  get suggestionsLength () {
    return this.suggestionsTarget.children.length
  }
  
  // Поиск по сотрудникам

  getUserByUuid(search) {
    const user = this.users.find(({ uuid }) => {
      return uuid === search;
    });
    return user ?? null;
  }

  nodeTypeExists(editor, typeName) {
    let found = false;
    editor.state.doc.forEach((node) => {
      if (node.type.name === typeName) {
        found = true;
      }
    });
    return found;
  }

  insertAssistantReport(event) {
    event.preventDefault()
    let reportHtml = event.currentTarget.dataset.reportHtml || ""
    const filesData = event.currentTarget.dataset.reportFiles
    reportHtml = reportHtml.trim()

    if (this.redactor) {
      this.redactor.chain()
        .focus()
        .insertContent(reportHtml)
        .createParagraphNear()
        .run()
    }

    if (filesData) {
      try {
        const files = JSON.parse(filesData)
        this.attachAssistantFiles(files)
      } catch (e) {
        console.error("Ошибка парсинга файлов:", e)
      }
    }
  }

  async attachAssistantFiles(filesArray) {
    const fileInput = this.element.querySelector('[data-multi-upload-target="input"]')
    if (!fileInput) return

    const multiUploadContainer = fileInput.closest('[data-controller*="multi-upload"]')
    if (!multiUploadContainer) return

    const dataTransfer = new DataTransfer()

    if (fileInput.files && fileInput.files.length > 0) {
      Array.from(fileInput.files).forEach(file => dataTransfer.items.add(file))
    }

    for (const fileInfo of filesArray) {
      try {
        const response = await fetch(fileInfo.url)
        if (!response.ok) continue
        const blob = await response.blob()
        const file = new File([blob], fileInfo.name, { type: blob.type })
        dataTransfer.items.add(file)
      } catch (error) {
        console.error("Ошибка скачивания файла:", error)
      }
    }

    fileInput.files = dataTransfer.files

    try {
      const application = window.Stimulus || this.application
      const controller = application.getControllerForElementAndIdentifier(multiUploadContainer, "multi-upload")

      if (controller && typeof controller.addFile === "function") {
        const mockEvent = {
          target: fileInput,
          currentTarget: fileInput,
          preventDefault: () => {}
        }
        controller.addFile(mockEvent)
      } else {
        fileInput.dispatchEvent(new Event('change', { bubbles: true }))
      }
    } catch (e) {
      console.warn("Не удалось вызвать метод контроллера:", e)
      fileInput.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  dispatchStateChange() {
    if (!this.redactor) return

    const event = new CustomEvent("editor:state-changed", {
      detail: {
        editorId: this.idValue,
        editor: this.redactor
      }
    })
    window.dispatchEvent(event)
  }

  handleSetLanguage = (event) => {
    if (event.detail.editorId !== this.idValue) return

    const { language } = event.detail
    this.redactor.chain().focus().updateAttributes('codeBlock', { language }).run()
  }
}
